import _ from "lodash";
import React, { useContext } from "react";
import classes from "./SubjectBenchMarkView.scss";
import UIBaseComponent from "UIComponents/UIBaseComponent";
import { Checkbox } from "@toddle-edu/ds-web";
import classNames from "classnames";
import { useI18n } from "Hooks";
import { getStyleStrippedText } from "Utils";

import HighlightSearchText from "../HighlightSearchText";

const SelectedValueContext = React.createContext();

const getChildrenContainerClass = ({ depth }) => {
  switch (depth) {
    case 0:
      return classes.treeContainer;
    case 1:
      return classes.treeContainer;
    case 2:
      return classes.treeContainer;
    case 3:
      return classes.h3TreeContainer;

    default:
      return classes.h4TreeContainer;
  }
};

const getChildrenContainerStyle = ({
  startDepth,
  depth,
  calculatedDepth,
  childrenContainerStyle = [],
}) => {
  const defaultChildrenContainerStyle = !_.isUndefined(calculatedDepth)
    ? {}
    : { marginTop: 0 };

  const customStyle = _.get(
    _.find(childrenContainerStyle, { depth }),
    "style",
    {}
  );

  return _.isEmpty(customStyle)
    ? defaultChildrenContainerStyle
    : {
        ...defaultChildrenContainerStyle,
        ...customStyle,
      };
};

const getNodeLabelClass = ({ depth }) => {
  switch (depth) {
    case 0:
      return classNames("text-label-2", classes.nodeLabel);
    case 1:
      return classNames("text-body-1", classes.nodeLabel);
    case 2:
      return classNames("text-body-1", classes.nodeLabel);

    case 3:
      return classNames("text-body-2", classes.nodeLabel);

    default:
      return classNames("text-body-2", classes.h4Label);
  }
};

const getNodeLabelStyle = ({
  depth,
  showCheckbox,
  nodeLabelStyle = [],
  isReverseCheckbox,
  dynamicLevelContainerStyle,
}) => {
  const defaultNodeLabelStyle = showCheckbox ? { cursor: "pointer" } : {};

  if (isReverseCheckbox && showCheckbox) {
    defaultNodeLabelStyle.flexDirection = "row-reverse";
  }

  let customStyle = _.get(_.find(nodeLabelStyle, { depth }), "style", {});
  if (!_.isEmpty(dynamicLevelContainerStyle)) {
    customStyle = {
      ...customStyle,
      ..._.get(dynamicLevelContainerStyle, depth, {}),
    };
  }
  return _.isEmpty(customStyle)
    ? defaultNodeLabelStyle
    : {
        ...defaultNodeLabelStyle,
        ...customStyle,
      };
};

const NodeTree = React.memo(props => {
  const {
    node,
    isTagging,
    loopDepth,
    startDepth,
    depthOffset,
    nodeLabelStyle,
    nodeSpanStyle,
    dynamicLevelContainerStyle,
    childrenContainerStyle,
    isReverseCheckbox,
    checkboxSize,
    searchTerm,
    checkboxItemStyle,
    checkboxContainerStyle,
    sortedNodeIds,
    labelStyle,
    isDsComponent,
    shouldCheckIsLeafProperty,
    allowParentTagging,
    showSearchLabel,
  } = props;

  const {
    id,
    label,
    children,
    depth,
    code,
    genericTags,
    tags,
    resolvedSubject,
    isArchived,
    parent,
  } = node;

  let isFirstElement = false;

  if (_.isNil(parent)) isFirstElement = true;

  const updatedDepth = depth + depthOffset;

  const childrenContainerClass = getChildrenContainerClass({
    depth: updatedDepth,
  });
  const nodeTags = !_.isEmpty(genericTags) ? genericTags : tags;

  // for some views, might need to offset the depth to start one or two level lower
  const calculatedDepth =
    (_.has(node, "depth") ? depth : loopDepth) + depthOffset;

  const updatedChildrenContainerStyle = getChildrenContainerStyle({
    startDepth,
    depth: updatedDepth + 1,
    calculatedDepth,
    childrenContainerStyle,
  });

  const isLeaf =
    shouldCheckIsLeafProperty && _.has(node, "isLeaf") && !_.isNull(node.isLeaf)
      ? node.isLeaf
      : _.get(children, "length", 0) == 0;

  if (isLeaf) {
    // this is for multi select functionality
    sortedNodeIds.push(id);
  }

  let _isArchived = isArchived;
  if (!_.isNil(resolvedSubject) && _.isNil(_isArchived))
    _isArchived = _.get(resolvedSubject, "isArchived", false);

  return (
    <div className={classes.nodeBody}>
      <NodeItem
        id={id}
        isFirstElement={isFirstElement}
        isArchived={_isArchived}
        label={label}
        isLeaf={isLeaf}
        isTagging={isTagging}
        depth={calculatedDepth}
        nodeLabelStyle={nodeLabelStyle}
        nodeSpanStyle={nodeSpanStyle}
        dynamicLevelContainerStyle={dynamicLevelContainerStyle}
        code={code}
        isReverseCheckbox={isReverseCheckbox}
        checkboxSize={checkboxSize}
        searchTerm={searchTerm}
        checkboxItemStyle={checkboxItemStyle}
        genericTags={nodeTags}
        checkboxContainerStyle={checkboxContainerStyle}
        labelStyle={labelStyle}
        isDsComponent={isDsComponent}
        showSearchLabel={showSearchLabel}
        allowParentTagging={allowParentTagging}
      />
      {!isLeaf && (
        <div
          className={childrenContainerClass}
          style={updatedChildrenContainerStyle}
        >
          {_.map(children, child => {
            return (
              <NodeTree
                node={child}
                key={child.id}
                isTagging={isTagging}
                startDepth={startDepth}
                loopDepth={loopDepth + 1}
                depthOffset={depthOffset}
                nodeLabelStyle={nodeLabelStyle}
                nodeSpanStyle={nodeSpanStyle}
                dynamicLevelContainerStyle={dynamicLevelContainerStyle}
                childrenContainerStyle={childrenContainerStyle}
                isReverseCheckbox={isReverseCheckbox}
                checkboxSize={checkboxSize}
                searchTerm={searchTerm}
                checkboxItemStyle={checkboxItemStyle}
                checkboxContainerStyle={checkboxContainerStyle}
                sortedNodeIds={sortedNodeIds}
                showSearchLabel={showSearchLabel}
                allowParentTagging={allowParentTagging}
              ></NodeTree>
            );
          })}
        </div>
      )}
    </div>
  );
});

const NodeItem = React.memo(props => {
  const { t } = useI18n(["common"]);
  const {
    label,
    depth,
    isLeaf,
    isTagging,
    id,
    nodeLabelStyle = [],
    code,
    isReverseCheckbox,
    nodeSpanStyle,
    dynamicLevelContainerStyle,
    checkboxContainerStyle,
    labelStyle,
    isDsComponent,
    isArchived,
    isFirstElement,
    searchTerm,
    showSearchLabel = false,
    allowParentTagging,
  } = props;

  const { selectedValues, toggleCheckbox } = useContext(SelectedValueContext);
  const depthMinusOne = depth - 1;
  const nodeLabelClass = getNodeLabelClass({
    depth: depthMinusOne >= 0 ? depthMinusOne : 0,
  });

  const isSelected = _.includes(selectedValues, id);
  const showCheckbox = (allowParentTagging || isLeaf) && isTagging;
  const updatedNodeLabelStyle = getNodeLabelStyle({
    depth,
    showCheckbox,
    nodeLabelStyle,
    isReverseCheckbox,
    dynamicLevelContainerStyle,
  });

  const bullet = classNames(
    { [classes.bullet]: !isReverseCheckbox },
    { [classes.bulletNoMargin]: isReverseCheckbox }
  );

  return (
    <div
      className={nodeLabelClass}
      style={updatedNodeLabelStyle}
      onClick={event => {
        if (showCheckbox) {
          const shouldSelectRange = event.shiftKey;
          toggleCheckbox({ id, isSelected, shouldSelectRange });
        }
      }}
      data-test-id="journal-postcreation-goal-searchitem"
    >
      {showCheckbox ? (
        <div className={bullet} style={checkboxContainerStyle}>
          <Checkbox
            isChecked={isSelected}
            size={"xx-small"}
            testId={"journal-postcreation-goal-searchitem"}
          />
        </div>
      ) : null}
      <span
        style={isDsComponent ? labelStyle : nodeSpanStyle}
        className={classes.spanStyle}
        key={id}
      >
        <div className={classes.labelContainer}>
          {showSearchLabel ? (
            <>
              <span>
                <HighlightSearchText text={label} highlight={searchTerm} />
              </span>
              <span className={classes.codeLabel}>
                <HighlightSearchText
                  text={code ? ` (${code})` : ``}
                  highlight={searchTerm}
                />
              </span>
            </>
          ) : (
            <>
              <span
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: label,
                    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                  }),
                }}
              />
              <span
                className={classes.codeLabel}
                dangerouslySetInnerHTML={{
                  __html: getStyleStrippedText({
                    text: code ? ` (${code})` : ``,
                    type: "SANITIZE_DANGER_HTML_PARAGRAPH",
                  }),
                }}
              />
            </>
          )}
          {isFirstElement && isArchived ? (
            <span className={classes.archived}>({t("common:archived")})</span>
          ) : null}
        </div>
      </span>
      {/* {`${label}`} */}
    </div>
  );
});

class SubjectBenchMarkView extends UIBaseComponent {
  constructor(props) {
    super(props);
    this.state = {
      ...this.state,
      lastSelectedNode: null,
    };
  }

  // an array of sequenced nodes
  sortedNodeIds = [];

  // get index of node from its id
  getIdxFromNodeId = id => {
    return _.indexOf(this.sortedNodeIds, id);
  };

  // get the start and end index of range we want to select
  getSelectionRange = id => {
    const currentIdx = this.getIdxFromNodeId(id),
      lastIdx = this.getIdxFromNodeId(this.state.lastSelectedNode);

    // should be in increasing order so as to iterate uniformly
    const selectionStartIdx = currentIdx > lastIdx ? lastIdx : currentIdx,
      selectionEndIdx = currentIdx > lastIdx ? currentIdx : lastIdx;

    return { selectionStartIdx, selectionEndIdx };
  };

  // check if all of nodes in range are selected
  isRangeSelected = ({ selectionStartIdx, selectionEndIdx, excludeId }) => {
    const { selectedValues } = this.props;
    let selected = true;

    for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
      const nodeId = this.sortedNodeIds[i];

      // exclude the beginning index that we just deselected
      // so that it doesn't trigger the break condition after this one
      if (_.isEqual(excludeId, nodeId)) continue;

      if (!_.includes(selectedValues, nodeId)) {
        selected = false;
        break;
      }
    }

    return selected;
  };

  toggleCheckbox = ({ id, isSelected, shouldSelectRange }) => {
    let { selectedValues } = this.props;
    const { lastSelectedNode } = this.state;

    // if Shift is pressed - range selection
    if (shouldSelectRange && lastSelectedNode) {
      const { selectionStartIdx, selectionEndIdx } = this.getSelectionRange(id);

      // [only while DESELECTING range]
      // by deselecting the beginning index, we deselected a part of the range
      // so we exclude it from checks so isRangeSelected doesn't return false
      const excludeId = _.includes(selectedValues, id)
        ? lastSelectedNode
        : null;

      // if range is already selected - deselect
      if (
        this.isRangeSelected({ selectionStartIdx, selectionEndIdx, excludeId })
      ) {
        const nodesToRemove = [];
        for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
          const nodeId = this.sortedNodeIds[i];
          nodesToRemove.push(nodeId);
        }

        selectedValues = _.filter(
          selectedValues,
          nodeId => !_.includes(nodesToRemove, nodeId)
        );
      } else {
        // else select the range
        for (let i = selectionStartIdx; i <= selectionEndIdx; i++) {
          const nodeId = this.sortedNodeIds[i];

          // prevent duplicates
          if (_.indexOf(selectedValues, nodeId) === -1)
            selectedValues = [...selectedValues, nodeId];
        }
      }

      // reset the beginning index
      this.setState({ lastSelectedNode: null });
    } else {
      // single selection
      if (isSelected) {
        selectedValues = _.filter(selectedValues, val => val != id);
      } else {
        selectedValues = [...selectedValues, id];
      }

      // this will be used as the beginning index for selection range
      this.setState({ lastSelectedNode: id });
    }
    this.updateValue(selectedValues);
  };

  renderView = () => {
    const {
      value,
      isTagging,
      startDepth,
      depthOffset,
      selectedValues,
      nodeLabelStyle,
      nodeSpanStyle,
      dynamicLevelContainerStyle,
      childrenContainerStyle,
      isReverseCheckbox,
      checkboxSize,
      searchTerm,
      checkboxItemStyle,
      checkboxContainerStyle,
      labelStyle,
      isDsComponent,
      shouldCheckIsLeafProperty,
      allowParentTagging,
      showSearchLabel = false,
    } = this.props;

    const childrenContainerClass = getChildrenContainerClass({
      depth: startDepth,
    });
    const updatedChildrenContainerStyle = getChildrenContainerStyle({
      depth: startDepth,
      startDepth,
      childrenContainerStyle,
    });
    return (
      <SelectedValueContext.Provider
        value={{ selectedValues, toggleCheckbox: this.toggleCheckbox }}
      >
        <div
          style={updatedChildrenContainerStyle}
          className={childrenContainerClass}
        >
          {_.map(value, node => {
            if (node) {
              return (
                <NodeTree
                  node={node}
                  key={node.id}
                  isTagging={isTagging}
                  loopDepth={startDepth}
                  startDepth={startDepth}
                  depthOffset={depthOffset}
                  nodeLabelStyle={nodeLabelStyle}
                  nodeSpanStyle={nodeSpanStyle}
                  dynamicLevelContainerStyle={dynamicLevelContainerStyle}
                  childrenContainerStyle={childrenContainerStyle}
                  isReverseCheckbox={isReverseCheckbox}
                  checkboxSize={checkboxSize}
                  searchTerm={searchTerm}
                  checkboxItemStyle={checkboxItemStyle}
                  checkboxContainerStyle={checkboxContainerStyle}
                  sortedNodeIds={this.sortedNodeIds}
                  labelStyle={labelStyle}
                  isDsComponent={isDsComponent}
                  shouldCheckIsLeafProperty={shouldCheckIsLeafProperty}
                  allowParentTagging={allowParentTagging}
                  showSearchLabel={showSearchLabel}
                ></NodeTree>
              );
            }
          })}
        </div>
      </SelectedValueContext.Provider>
    );
  };
}

NodeTree.displayName = "NodeTree";
NodeItem.displayName = "NodeItem";

SubjectBenchMarkView.defaultProps = {
  ...UIBaseComponent.defaultProps,
  isTagging: false,
  startDepth: 0,
  selectedValues: [],
  depthOffset: 0,
  isReverseCheckbox: false,
  searchTerm: "",
  checkboxItemStyle: {},
  nodeSpanStyle: {},
  dynamicLevelContainerStyle: {},
  checkboxContainerStyle: {},
  labelStyle: {},
  isDsComponent: false,
  shouldCheckIsLeafProperty: true,
  allowParentTagging: false,
};

SubjectBenchMarkView.propTypes = {
  ...UIBaseComponent.propTypes,
};

export default SubjectBenchMarkView;
